/**
 * The arithmetic behind a two-finger pinch on the page column.
 *
 * **Separate from `pdfPageLayout.ts` on purpose.** That module answers where a
 * page goes and what it costs to draw; this one answers what a gesture is
 * asking for. They are consulted at different moments — the layout numbers on
 * every render, these only while a gesture is live.
 *
 * **Separate from `zoomMath.ts` for the reason those two headers already give.**
 * The image viewer translates and scales one element inside an
 * `overflow-hidden` box. This is a *scrolled* column: the browser owns the pan
 * limits, and the content box sits inside the stage's padding.
 *
 * **Nothing below predicts where a page will land**, and that is deliberate
 * even now that the column does scale uniformly. It did not always: the gap
 * between pages was a flat 24px at every zoom, so a six-page filing at 3.3×
 * came out 8598px tall where multiplying gave 8838 — and every prediction built
 * on the ratio was out by one gutter per page. `PdfStage` scales the gutter for
 * a different reason (a magnified column and a drawn one have to be the same
 * shape, or the commit visibly shifts everything above the reader), and this
 * module does not depend on it having done so. The gesture is accumulated as a
 * transform, and the position it has to be restored to afterwards is expressed
 * against **a page**, measured once the new layout exists.
 *
 * Here rather than in `PdfStage` because none of it is observable through
 * jsdom: nothing lays out, so every rect is 0×0.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The gesture so far, as a transform on the content box with a `0 0` origin.
 *
 * `scale` is relative to the scale the pages are actually drawn at, so
 * `IDENTITY` is "no gesture" and is what the box must be left in at rest.
 */
export interface View {
  scale: number;
  x: number;
  y: number;
}

export const IDENTITY: View = { scale: 1, x: 0, y: 0 };

/** How far apart two fingers are. */
export function spread(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** The point the gesture is centred on. */
export function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * How much bigger this sample is than the last one.
 *
 * **A start distance of zero answers 1 rather than dividing.** Two fingers can
 * land on the same pixel — rare, entirely possible, and an `Infinity` here
 * reaches the DOM as `scale(Infinity)`, which does not fail loudly.
 */
export function pinchRatio(now: number, start: number): number {
  if (start <= 0) return 1;
  return now / start;
}

/**
 * Fold one sample of a pinch into the gesture so far.
 *
 * **Incremental, and it has to be.** The first version of this scaled by a
 * ratio measured from the start of the gesture while re-deriving the pivot from
 * the *current* finger midpoint, and the two disagree the moment the midpoint
 * moves: at 3×, a 10px drift of the fingers threw the page 20px sideways. That
 * was the jerk. Each sample now carries its own small ratio about its own
 * anchor, and the result accumulates — which is what `ZoomStage` does, for the
 * same reason.
 *
 * `anchor` is the finger midpoint expressed from the content box's origin —
 * stage-relative, plus the scroll offset, less the stage's padding. `drift` is
 * how far that midpoint travelled since the last sample, and carrying it is
 * what lets two fingers move the document at the same time as sizing it.
 *
 * A content point `p` sits at `origin − scroll + x + scale · p`, so holding the
 * anchor still across a ratio `k` gives `x' = anchor(1 − k) + k · x`.
 */
export function pinchView(view: View, anchor: Point, ratio: number, drift: Point): View {
  return {
    scale: view.scale * ratio,
    x: anchor.x * (1 - ratio) + ratio * view.x + drift.x,
    y: anchor.y * (1 - ratio) + ratio * view.y + drift.y,
  };
}

/**
 * Where a point sits inside a box, as a fraction of it plus whatever is left
 * over.
 *
 * **The remainder is the whole reason this returns two things.** A fraction
 * alone is exact for a point on a page and wrong for a point in the gap between
 * two, because a fraction scales and the gap does not. Splitting the position
 * into "this far into the page, then this many pixels further" keeps the part
 * that scales apart from the part that never does.
 */
export function anchorIn(rect: Rect, point: Point): { fraction: Point; remainder: Point } {
  const share = (value: number, start: number, size: number): { f: number; r: number } => {
    if (size <= 0) return { f: 0, r: value - start };

    const f = Math.min(1, Math.max(0, (value - start) / size));
    return { f, r: value - (start + f * size) };
  };

  const x = share(point.x, rect.left, rect.width);
  const y = share(point.y, rect.top, rect.height);

  return { fraction: { x: x.f, y: y.f }, remainder: { x: x.r, y: y.r } };
}

/**
 * How far to scroll so an anchored point lands back under the fingers.
 *
 * `rect` is the anchor page **as it has just been laid out**, measured relative
 * to the stage. Nothing here predicts that rect: the caller reads it after the
 * new scale has been applied, which is what makes the unscaled gaps somebody
 * else's problem. The answer is a delta to add to the current scroll offsets,
 * and the browser clamps it to what actually exists.
 */
export function scrollCorrection(
  rect: Rect,
  anchor: { fraction: Point; remainder: Point },
  focus: Point,
): Point {
  return {
    x: rect.left + anchor.fraction.x * rect.width + anchor.remainder.x - focus.x,
    y: rect.top + anchor.fraction.y * rect.height + anchor.remainder.y - focus.y,
  };
}
